import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';

const nodeItems = [
  { key: 'stored', label: 'Stored' },
  { key: 'effective', label: 'Effective' },
  { key: 'runtime', label: 'Runtime' },
];

export const TemplateStoredEffectiveComparator = ({ compareData, loading }) => {
  if (loading) {
    return <Card className="px-4 py-6 text-sm text-muted-foreground" data-testid="template-stored-effective-compare-loading">A comparar stored / effective / runtime...</Card>;
  }

  if (!compareData?.stored?.id) return null;

  return (
    <Card className="overflow-hidden" data-testid="template-stored-effective-comparator">
      <div className="border-b bg-slate-50 px-4 py-3">
        <p className="text-sm font-semibold">Comparar `stored vs effective vs runtime`</p>
        <p className="text-[11px] text-muted-foreground">Leitura governada antes de editar, guardar ou consolidar.</p>
      </div>

      <div className="grid gap-3 px-4 py-4 xl:grid-cols-3" data-testid="template-stored-effective-comparator-grid">
        {nodeItems.map((item) => {
          const node = compareData[item.key] || {};
          return (
            <div key={item.key} className="rounded-2xl border bg-white p-3" data-testid={`template-stored-effective-${item.key}-card`}>
              <div className="flex items-center justify-between gap-2">
                <Label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{item.label}</Label>
                {item.key === 'effective' && compareData.effective?.resolved_rule ? <Badge variant="outline">{compareData.effective.resolved_rule}</Badge> : null}
                {item.key === 'runtime' && compareData.runtime?.data_source ? <Badge variant="outline">{compareData.runtime.data_source}</Badge> : null}
              </div>
              <p className="mt-2 text-sm font-semibold">{node.name || node.resolved_template_name || '—'}</p>
              <div className="mt-3 space-y-1 text-[11px] text-muted-foreground">
                <p>Template: {node.id || node.resolved_template_id || '—'}</p>
                <p>Localização: {node.location_name || node.location_id || node.runtime_location_id || 'Global'}</p>
                <p>Módulo: {Array.isArray(node.module_group) ? node.module_group.join(', ') || '—' : node.render_pipeline || '—'}</p>
                <p>Elementos: {node.layout?.element_count ?? '—'}</p>
                <p>Bindings: {node.layout?.variable_binding_count ?? '—'}</p>
                <p>Datasources: {node.layout?.datasource_count ?? '—'}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="grid gap-3 border-t px-4 py-4 xl:grid-cols-[0.9fr_1.1fr]">
        <div className="rounded-2xl border bg-white p-3" data-testid="template-stored-effective-inheritance-card">
          <Label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Herança / override</Label>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <Badge variant={compareData.inheritance?.same_template ? 'outline' : 'secondary'}>{compareData.inheritance?.same_template ? 'Stored = Effective' : 'Stored ≠ Effective'}</Badge>
            <Badge variant="outline">{compareData.inheritance?.location_inherited ? 'Localização herdada' : 'Localização guardada'}</Badge>
            <Badge variant="outline">{compareData.inheritance?.module_inherited ? 'Módulo herdado' : 'Módulo guardado'}</Badge>
            <Badge variant="outline">Δ elementos {compareData.diff_summary?.element_delta ?? 0}</Badge>
          </div>
        </div>

        <div className="rounded-2xl border bg-white p-3" data-testid="template-stored-effective-risks-card">
          <Label className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Riscos / ajustes de preview</Label>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            {(compareData.preview_risks?.root_causes || []).length > 0 ? compareData.preview_risks.root_causes.map((cause) => (
              <Badge key={cause} variant="secondary">{cause}</Badge>
            )) : <Badge variant="outline">Sem alertas principais</Badge>}
            {(compareData.runtime?.adjustments || []).map((adjustment) => (
              <Badge key={adjustment} variant="outline">{adjustment}</Badge>
            ))}
          </div>
          <div className="mt-3 grid gap-2 md:grid-cols-2 text-[11px] text-muted-foreground">
            <p>Stored overlaps: {compareData.preview_risks?.stored?.overlap_candidates ?? 0}</p>
            <p>Effective overlaps: {compareData.preview_risks?.effective?.overlap_candidates ?? 0}</p>
            <p>Stored unresolved: {(compareData.preview_risks?.stored?.unresolved_bindings || []).length}</p>
            <p>Effective unresolved: {(compareData.preview_risks?.effective?.unresolved_bindings || []).length}</p>
          </div>
        </div>
      </div>
    </Card>
  );
};